/**
 * Completion content for the "follow-up-on-services" activity.
 * Confirms follow-up communication happened and captures the service outcome.
 * Creates a Communication (progress log) to record the follow-up interaction.
 * @agentic connections
 */

import { useForm } from "@mantine/form";
import { Alert, Select, Stack, Text, Textarea } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type {
  Patient,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";
import { COMMUNICATION_MEDIUM_SELECT_OPTIONS } from "@benchmark/core/constants";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { findBenchmarkPractitionerRole } from "@benchmark/core/services";
import { logActivityProgress } from "@benchmark/core/actions";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import { showWarningNotification } from "@benchmark/ui/utils";
import type { CompletionContentProps } from "./DefaultCompletionContent";
import { CONFIG } from "@/app/config";

const CONTACT_METHOD_OPTIONS = COMMUNICATION_MEDIUM_SELECT_OPTIONS.filter(
  (o) => o.value === "phone" || o.value === "text",
);

const REFERRAL_OUTCOME_OPTIONS = [
  { value: "completed", label: "Appointment Completed" },
  { value: "cancelled", label: "Appointment Cancelled" },
  { value: "rescheduled", label: "Rescheduled" },
  { value: "no-show", label: "No-Show" },
];

/**
 * Form state for the follow-up-on-services completion wizard.
 * @agentic connections
 */
type FollowUpServicesFormData = {
  outcome: string;
  contactMethod: string;
  notes: string;
};

/**
 * Completion form for follow-up-on-services: outcome + contact method + notes.
 * @agentic connections
 */
export const FollowUpOnServicesCompletionContent = ({
  task,
  onComplete,
  onCancel,
  onDirtyChange,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextActivityAppliedRule,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  isResolvingNextTaskOwner = false,
}: CompletionContentProps) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const patientId = getResourceIdFromReference(task.for?.reference, "Patient");
  const { data: patientResourceRaw } = usePatientResource(patientId);
  const patient = patientResourceRaw as Patient | undefined;
  const [practitionerRole, setPractitionerRole] = useState<
    PractitionerRole | undefined
  >();

  useEffect(() => {
    if (!profile?.id) return;
    findBenchmarkPractitionerRole(medplum, {
      reference: `Practitioner/${profile.id}`,
      benchmarkOrganizationId: CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    })
      .then(setPractitionerRole)
      .catch(() => setPractitionerRole(undefined));
  }, [medplum, profile?.id]);

  const form = useForm<FollowUpServicesFormData>({
    initialValues: {
      outcome: "",
      contactMethod: "",
      notes: "",
    },
    validate: {
      outcome: (value) => (!value ? "Referral outcome is required" : null),
    },
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();

  const steps = useMemo(
    () => [
      {
        id: "details",
        title: "Details",
        subtitle: "Capture referral follow-up",
        requiredFields: ["outcome"],
      },
      {
        id: "assignment",
        title: "Assignment",
        subtitle: "Review next task assignment",
      },
    ],
    [],
  );

  const handleSubmit = useCallback(
    async (values: FollowUpServicesFormData) => {
      if (!values.outcome) return;

      setSubmitting(true);
      setError(null);

      try {
        const outcomeLabel =
          REFERRAL_OUTCOME_OPTIONS.find((o) => o.value === values.outcome)
            ?.label ?? values.outcome;

        if (task.id && patient && profile && practitionerRole) {
          try {
            await logActivityProgress(medplum, {
              task,
              progressData: {
                contactMethod:
                  (values.contactMethod as "phone" | "text") || undefined,
                outcome: "general-progress",
                outcomeDisplay: `Referral Follow-Up: ${outcomeLabel}`,
                notes: values.notes || undefined,
                snoozeBehavior: { type: "none" },
              },
              patient,
              practitioner: profile,
              practitionerRole,
            });
          } catch {
            showWarningNotification(
              "Follow-up interaction could not be logged, but completion will proceed.",
            );
          }
        }

        const statusReason = [
          `Outcome: ${values.outcome}`,
          values.notes && `Notes: ${values.notes}`,
        ]
          .filter(Boolean)
          .join(". ");

        await onComplete(statusReason);
        await teeForTask({
          task,
          minutes: careMinutes,
          method: "task-completion",
          notes: values.notes || undefined,
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to complete task",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      onComplete,
      task,
      patient,
      profile,
      practitionerRole,
      medplum,
      teeForTask,
      careMinutes,
    ],
  );

  useEffect(() => {
    onDirtyChange?.(
      !!form.values.outcome ||
        !!form.values.contactMethod ||
        form.values.notes.trim().length > 0 ||
        !!nextTaskOwnerOverride ||
        !!careMinutes,
    );
  }, [form.values, nextTaskOwnerOverride, careMinutes, onDirtyChange]);

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={handleSubmit}
        isSubmitting={submitting}
        submitText="Complete Follow-Up"
        onCancel={onCancel}
      >
        <FormStep id="details">
          <Stack gap="md">
            <Text size="sm">
              Confirm the follow-up was completed and record the referral
              outcome.
            </Text>

            <Select
              label="Referral Outcome"
              placeholder="Select outcome..."
              required
              data={REFERRAL_OUTCOME_OPTIONS}
              value={form.values.outcome || null}
              onChange={(value) => form.setFieldValue("outcome", value ?? "")}
              error={form.errors.outcome}
            />

            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Contact Method
              </Text>
              <CardSelect
                data={CONTACT_METHOD_OPTIONS}
                value={form.values.contactMethod || null}
                onChange={(value) => {
                  const singleValue = Array.isArray(value) ? value[0] : value;
                  form.setFieldValue("contactMethod", singleValue ?? "");
                }}
                columns={2}
                compact
                aria-label="Select contact method"
              />
            </Stack>

            <Textarea
              label="Notes (optional)"
              placeholder="Add follow-up details..."
              {...form.getInputProps("notes")}
              autosize
              minRows={2}
              maxRows={4}
            />
          </Stack>
        </FormStep>

        <FormStep id="assignment">
          <Stack gap="md">
            <NextTaskOwnerSection
              nextActivityLabel={nextActivityLabel}
              defaultOwner={nextActivityDefaultOwner}
              appliedRule={nextActivityAppliedRule}
              overrideOwner={nextTaskOwnerOverride}
              onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
              isResolving={isResolvingNextTaskOwner}
            />
            <CareTimeActivityDurationField
              minutes={careMinutes}
              onMinutesChange={setCareMinutes}
            />
          </Stack>
        </FormStep>
      </SteppedForm>
    </Stack>
  );
};
